import type {SyntheticEvent} from "react";
import {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {Select} from "@radix-ui/themes";

import styles from "../styles/LanguageTool.module.scss";
import NotificationManager from "../../notifications/NotificationManager";
import {useStores} from "../../../stores/useStores";
import useTheme from "../../radix/useTheme";

const DropDownItems = observer((props: {
    items: Array<string>
}) => {
    const {i18nStore} = useStores();

    return <>
        {
            props.items.map((locale) => {
                const lang = i18nStore.getLanguage(locale);
                return <Select.Item className={"notranslate"}
                                      value={locale}
                                      key={locale}
                >
                    {lang}
                </Select.Item>
            })
        }
    </>;
});

const SupportedDropDownItems = observer(() => {
    const {i18nStore} = useStores();

    const ssLocales = i18nStore.organizationSupportedLocales;
    const asLocales = i18nStore.appSupportedButNotorganizationSupportedLocales;

    return <>
        {/* school supported */}
        <DropDownItems items={ssLocales} />
        {/* app supported */}
        <DropDownItems items={asLocales} />
    </>;
})

const GoogleDropDownItems = observer(() => {
    const {i18nStore} = useStores();

    const [googleItems, setGoogleItems] = useState<Array<React.ReactElement | undefined>>([]);
    const masterTimeoutFinished = useRef(false);

    useEffect(() => {
        function getGoogleLanguages() {
            const firstValue = document.querySelector(".goog-te-combo option:nth-child(1)");
            if (masterTimeoutFinished.current) {
                NotificationManager.error("Google Translate failed to load! Some translation features may not be available.");
                return;
            } else if (!firstValue) {
                setTimeout(getGoogleLanguages, 100);
            } else {
                const googleDropDownItems = i18nStore.unsupportedLocales.map((loc: string) => {
                    const query = "select > option[value='" + loc + "']";
                    const el = document.querySelector(query);
                    if (el !== null) {
                        const language = el.textContent;
                        return <Select.Item key={loc} className={"notranslate"} value={loc}>
                            {language}
                        </Select.Item>;
                    }
                });

                setGoogleItems(googleDropDownItems);
            }
        }

        getGoogleLanguages();
    }, []);

    useEffect(() => {
        setTimeout(() => {
            masterTimeoutFinished.current = true;
        }, 5000);
    }, []);

    return <>{googleItems}</>;
});

export const LanguageDropDown = observer((props: {
    handleSelect?: (e?: SyntheticEvent<unknown>) => void,
}) => {
    const radixTheme = useTheme();
    const {i18nStore} = useStores();

    const handleSelect = (e) => {
        i18nStore.setLocaleByUser(e);
        if (typeof props.handleSelect === 'function') {
            props.handleSelect(e);
        }
    }

    // @ts-ignore
    return <Select.Root asChild={true} value={i18nStore.currentLanguageString} onValueChange={handleSelect}>
        <div className={"notranslate"}>
            <Select.Trigger style={{width: "100%"}} data-testid={"language-dropdown-trigger"} />
            <Select.Content
                container={radixTheme.portalContainer?.current}
                className={styles.dropdownMenu}
                role={"menu"}
                position={"popper"}
            >
                <SupportedDropDownItems/>
                <GoogleDropDownItems/>
            </Select.Content>
        </div>
    </Select.Root>
})
